import { useHistory } from "react-router-dom";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { SessionContext } from "../../common/contexts/SessionContext";
import { useContext, useMemo } from "react";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { GetPersons } from "../../common/misc/EntityRenders/EntityRenderer";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { EntityTableTabs } from "../../common/tables/Tabs/EntityTableTabs";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { getAddRoute, getDetailLink, getIndexRoute } from "../../main/Routing";
import styles from "../../common/tables/GenericVirtualizedTable/commons.module.css";
import {
  CustomType,
  CustomTypeFilters,
  CustomTypeFiltersTranslator,
  customTypeConstants,
} from "../../api/CustomTypes/CustomTypes";
import { CustomTypeFilterForm, CustomTypesFilterBar } from "../../Customization/CustomTypes/Table/CustomTypesFilterBar";
import { hierarchyConstants, inventoryItemsConstants } from "../../api/Inventories";
import { LinkButton } from "../../common/buttons/LinkButton/LinkButton";
import { NotSet } from "../../common/misc/UIconstants";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityOrderBy, InferSortByOptions, ISearchTermParametersDefaults } from "../../api/GenericTypes";
import { CustomTypeLabelRenderer } from "../../Customization/CustomTypes/generics/CustomTypeRenderer";

export const switchCustomTypesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomTypeFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomTypeFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "LAYOUT":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-isHierarchyRoot",
          property: sort.property,
        };
      case "INVENTORY_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-inventoryName",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-inventoryName",
          property: "NAME",
        };
    }
  });
};
export const defaultInventoriesFilters: CustomTypeFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
  ...ISearchTermParametersDefaults,
};

export const useInventoriesTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"inventories">) => {
  const defaults: ColumnsSettings<CustomType> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-inventoryName": {
        pos: 1,
        active: true,
        header: fieldLabels.inventoryName,
        property: "inventoryName",
      },
      "default-inventoryDescription": {
        pos: 2,
        active: true,
        header: fieldLabels.inventoryDescription,
        property: "inventoryDescription",
      },
      "default-isHierarchyRoot": {
        pos: 5,
        active: true,
        header: "Layout",
        property: "isHierarchyRoot",
      },
      "default-name": { pos: 3, active: false, header: hierarchyConstants.childType, property: "name" },
      "default-description": {
        pos: 4,
        active: false,
        header: hierarchyConstants.childTypeDescription,
        property: "description",
      },
      "default-enteredOn": { pos: 6, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 7, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 8, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 9, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [
      fieldLabels.enteredBy,
      fieldLabels.enteredOn,
      fieldLabels.id,
      fieldLabels.inventoryDescription,
      fieldLabels.inventoryName,
      fieldLabels.modifiedBy,
      fieldLabels.modifiedOn,
    ]
  );

  const tabStoreDefaults: TableTabsDict<CustomType, CustomTypeFilters, CustomTypeFilterForm> = useMemo(() => {
    let def: TableTabsDict<CustomType, CustomTypeFilters, CustomTypeFilterForm> = {
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultInventoriesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { entityTypes: ["InventoryItem"], isInventory: true },
          sidebarFilters: { entityType: ["InventoryItem"] },
        },
      },
    };
    return def;
  }, []);

  return { defaults, tabStoreDefaults };
};

export const useInventoriesTableColumns = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"inventories">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomType> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.inventoryName,
        id: "default-inventoryName",
        accessor: (row) =>
          row.inventoryName ? (
            <div className={`${styles.container_ellipsis} gap-5`}>
              <div className={`${styles.container_ellipsis}`}>
                <span className={styles.label_highlight}>
                  <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.inventoryName}
                </span>
              </div>
              {!row.isEnabled && (
                <div>
                  <label className="label label-soft-danger" title={"Disabled"}>
                    Disabled
                  </label>
                </div>
              )}
            </div>
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("INVENTORY_NAME", sort, setSort),
      },
      {
        Header: fieldLabels.inventoryDescription,
        id: "default-inventoryDescription",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.inventoryDescription}
          </div>
        ),
        minWidth: 150,
        width: 300,
        align: "left",
      },
      {
        Header: hierarchyConstants.childType,
        id: "default-name",
        accessor: (row) => <CustomTypeLabelRenderer entity={{ customType: row }} style={{ cursor: "pointer" }} />,
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: hierarchyConstants.childTypeDescription,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: "normal",
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.description}
          </div>
        ),
        minWidth: 150,
        width: 300,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        Header: "Layout",
        id: "default-isHierarchyRoot",
        accessor: (row) => (
          <div className="flex align-center gap-5">
            <LucideIcon
              name={row.isHierarchyRoot ? hierarchyConstants.hierarchyLayoutIcon : hierarchyConstants.flatLayoutIcon}
              color="var(--primary)"
            />
            {row.isHierarchyRoot ? "hierarchical" : "list"}
          </div>
        ),
        minWidth: 150,
        width: 150,
        align: "left",
        ...getAsyncSortingFn("LAYOUT", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.id,
    fieldLabels.inventoryDescription,
    fieldLabels.inventoryName,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    setSort,
    sort,
  ]);
  return { columns };
};

export const InventoriesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"inventories">) => {
  const history = useHistory();
  const { route, session } = useContext(SessionContext);

  const { defaults, tabStoreDefaults } = useInventoriesTableDefaults({ fieldLabels });

  const { resultsCount, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomType>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<CustomType, CustomTypeFilters, CustomTypeFilterForm>({
    resource: customTypeConstants.resource,
    defaults: tabStoreDefaults,
    keyOverride: "tableTabs-inventories",
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomTypesDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useInventoriesTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <LinkButton
            className="btn btn-default"
            linkTo={route(getIndexRoute(inventoryItemsConstants.frontendIndexRoute))}
            title={`Show all ${entityConstants.entitySingular} in table`}
          >
            <LucideIcon name="table-2" /> Show all {inventoryItemsConstants.entityPlural}
          </LinkButton>
          <LinkButton
            className="btn btn-primary"
            linkTo={`${route(getAddRoute(customTypeConstants.frontendIndexRoute))}?entityType=InventoryItem`}
            title="Create custom types"
            disabled={!session?.permissions.administer_custom_field_schemas}
          >
            <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
          </LinkButton>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar defaultSize={0}>
          <CustomTypesFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
            hideEntityTypeFilter
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={filters}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomType, CustomTypeFilters>
                filters={filters}
                excludeFilters={{
                  entityTypes: () => false,
                  isInventory: () => false,
                  excludeDisabled: (value) => !value,
                }}
                translatorConsts={CustomTypeFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
            </EntityTable.Controls>
            <EntityTable.Body<CustomType, CustomTypeFilters>
              functionRef={functionRef}
              entityConstants={customTypeConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
              onRowClick={(row) =>
                !!row.isHierarchyRoot
                  ? history.push(`${route(getDetailLink(entityConstants.frontendIndexRoute, row.id))}/hierarchy`)
                  : history.push(`${route(getDetailLink(entityConstants.frontendIndexRoute, row.id))}/items`)
              }
              disableCheckboxes
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
